import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase/firebaseConfig';
import { getUserData } from './services/auth/authService';
import { doc, setDoc } from 'firebase/firestore';
import { COLLECTIONS } from './constants/collections';
import { PERMISSIONS } from './constants/permissions';

// Layout
import MainLayout from './components/layout/MainLayout';

// Pages
import Login from './pages/auth/Login';
import Pricing from './pages/auth/Pricing';
import Register from './pages/auth/Register';
import Dashboard from './pages/common/Dashboard';
import UserProfile from './pages/common/UserProfile';
import Notifications from './pages/common/Notifications';
import ReservationManagement from './pages/coordinator/ReservationManagement';
import VehiclePlanning from './pages/coordinator/VehiclePlanning';
import TransporterManagement from './pages/coordinator/TransporterManagement';
import UserManagement from './pages/admin/UserManagement';
import PermissionManagement from './pages/admin/PermissionManagement';
import Settings from './pages/admin/Settings';
import ReservationTracking from './pages/operations/ReservationTracking';
import DockStatus from './pages/coordinator/DockStatus';
import DockManagement from './pages/coordinator/DockManagement';
import TransporterDashboard from './pages/transporter/Dashboard';
import TransporterReservations from './pages/transporter/TransporterReservations';
import MyVehicles from './pages/transporter/MyVehicles';
import MyDrivers from './pages/transporter/MyDrivers';
import TestRunner from './pages/debug/TestRunner';
import GateEntryExit from './pages/gate/GateEntryExit';
import LoadingList from './pages/dock/LoadingList';
import DockStatusScreen from './pages/dock/DockStatusScreen';
import CarrierPerformanceReport from './pages/reports/CarrierPerformanceReport';
import Button from './components/shared/Button';

// Contexts
import { ToastProvider, ToastContext } from './context/ToastContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import ToastContainer from './components/shared/ToastContainer';

const NotFound = () => (
    <div className="container" style={{ paddingTop: '100px', textAlign: 'center' }}>
        <h2>404</h2>
        <p>Sayfa bulunamadı.</p>
    </div>
);

const ComingSoon = ({ title }) => (
    <div className="container" style={{ paddingTop: '100px', textAlign: 'center' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏗️</div>
        <h2>{title}</h2>
        <p className="text-secondary">Bu sayfa şu an yapım aşamasındadır. Çok yakında hizmetinizde olacak.</p>
        <div className="mt-xl">
            <Button onClick={() => window.history.back()} variant="secondary">Geri Dön</Button>
        </div>
    </div>
);

// Helper for protected routes - Moved to components
import ProtectedRoute from './components/auth/ProtectedRoute';

// Helper for standalone protected routes (No Sidebar/Header) - Moved outside
const StandaloneRoute = ({ user, userData, children, roles = [] }) => {
    if (!user) return <Navigate to="/login" />;

    const isSuperUser = userData?.email === 'mert.akan@zaparmy.com.tr';

    if (roles.length > 0 && userData && !isSuperUser && !roles.includes(userData.role)) {
        return <Navigate to="/" />;
    }

    return children;
};

const AppContent = () => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Toast Hook consumption
    const { success, error: toastError } = React.useContext(ToastContext);
    const { toggleTheme } = useTheme();


    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                try {
                    const data = await getUserData(currentUser.uid);
                    if (data) {
                        setUserData(data);
                        // Auto-upgrade Mert Akan to coordinator if not already
                        if (currentUser.email === 'mert.akan@zaparmy.com.tr' && data.role !== 'coordinator') {
                            const { doc, updateDoc } = await import('firebase/firestore');
                            await updateDoc(doc(db, COLLECTIONS.USERS, currentUser.uid), { role: 'coordinator' });
                            setUserData(prev => ({ ...prev, role: 'coordinator' }));
                        }
                    } else {
                        console.warn('No user document found for UID:', currentUser.uid);
                        setUserData({
                            fullName: currentUser.displayName || 'Yeni Kullanıcı',
                            email: currentUser.email,
                            role: 'coordinator',
                            status: 'repair_needed'
                        });
                    }
                } catch (error) {
                    console.error('User data fetch error:', error);
                    setUserData({
                        fullName: currentUser.displayName || 'Yeni Kullanıcı',
                        email: currentUser.email,
                        role: 'coordinator',
                        status: 'error'
                    });
                } finally {
                    setLoading(false);
                }
            } else {
                setUser(null);
                setUserData(null);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const handleRepairAccount = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const newUserData = {
                fullName: user.displayName || 'Yeni Kullanıcı',
                email: user.email,
                role: 'coordinator',
                tenantId: `tenant_${user.uid}`,
                tenantName: 'RTC',
                createdAt: new Date(),
                status: 'active',
                isActive: true
            };

            // 1. Create Tenant First
            await setDoc(doc(db, COLLECTIONS.TENANTS, newUserData.tenantId), {
                name: newUserData.tenantName,
                adminId: user.uid,
                createdAt: new Date(),
                isActive: true,
                plan: 'starter'
            });

            // 2. Create User Documents
            // Tenant User (Full Profile)
            await setDoc(doc(db, COLLECTIONS.TENANTS, newUserData.tenantId, 'users', user.uid), newUserData);

            // 3. Clear storage or any cached data if needed
            setUserData(newUserData);
            success('Hesap başarıyla onarıldı! Lojistik paneline yönlendiriliyorsunuz.');
        } catch (err) {
            console.error('Repair failed:', err);
            toastError('Profil oluşturulurken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center" style={{ height: '100vh', background: 'var(--bg-secondary)', gap: '20px' }}>
                <div className="badge badge-pending">Sistem Yükleniyor...</div>
                <div className="text-secondary">Lütfen bekleyin...</div>
            </div>
        );
    }

    if (userData?.status === 'repair_needed' || userData?.status === 'error') {
        return (
            <div className="flex flex-col justify-center items-center p-xl" style={{ height: '100vh', background: 'var(--bg-secondary)', textAlign: 'center' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔧</div>
                <h2 className="mb-md">Profil Verisi Bulunamadı</h2>
                <p className="text-secondary mb-xl" style={{ maxWidth: '500px' }}>
                    Sisteme giriş yaptınız ancak profil bilgileriniz veritabanında henüz oluşturulmamış.
                    Aşağıdaki butona tıklayarak profilinizi hemen oluşturabilirsiniz.
                </p>
                <div className="flex gap-md">
                    <Button onClick={handleRepairAccount} variant="primary" size="lg">Profilimi Şimdi Oluştur</Button>
                    <Button onClick={() => auth.signOut()} variant="secondary">Çıkış Yap</Button>
                </div>
            </div>
        );
    }

    return (
        <Router>
            <Routes>
                {/* Public Routes */}
                <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/register" element={<Register />} />

                {/* Standalone Dock Routes */}
                <Route path="/dock/status/:dockId" element={<DockStatusScreen userData={userData} />} />
                <Route path="/dock/management" element={<StandaloneRoute user={user} userData={userData} roles={['coordinator', 'dock_staff', 'consultant']}><DockManagement userData={userData} /></StandaloneRoute>} />

                {/* Dashboard - Accessible to all (content filters inside) */}
                <Route
                    path="/"
                    element={
                        <ProtectedRoute user={user} userData={userData}>
                            {userData?.role === 'transporter' ? (
                                <TransporterDashboard userData={userData} />
                            ) : (
                                <Dashboard userData={userData} />
                            )}
                        </ProtectedRoute>
                    }
                />

                <Route path="/profile" element={<ProtectedRoute user={user} userData={userData}><UserProfile userData={userData} /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute user={user} userData={userData}><Notifications userData={userData} /></ProtectedRoute>} />

                {/* Coordinator & Shared Routes - PERMISSION PROTECTED */}
                <Route
                    path="/coordinator/transporters"
                    element={
                        <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.TRANSPORTERS}>
                            <TransporterManagement userData={userData} />
                        </ProtectedRoute>
                    }
                />

                <Route path="/coordinator/planning" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.PLANNING}>
                        <VehiclePlanning userData={userData} />
                    </ProtectedRoute>
                } />

                <Route path="/coordinator/reservation-management" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.RESERVATIONS}>
                        <ReservationManagement userData={userData} />
                    </ProtectedRoute>
                } />

                <Route path="/coordinator/docks" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.DOCKS}>
                        <DockStatus userData={userData} />
                    </ProtectedRoute>
                } />

                {/* Transporter Routes */}
                <Route path="/transporter/vehicles" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.VEHICLES}>
                        <MyVehicles userData={userData} />
                    </ProtectedRoute>
                } />

                <Route path="/transporter/drivers" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.DRIVERS}>
                        <MyDrivers userData={userData} />
                    </ProtectedRoute>
                } />

                <Route path="/transporter/reservations" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.MY_RESERVATIONS}>
                        <TransporterReservations userData={userData} />
                    </ProtectedRoute>
                } />

                {/* Operations Routes */}
                <Route path="/gate/process" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.GATE_PROCESS}>
                        <GateEntryExit userData={userData} />
                    </ProtectedRoute>
                } />

                <Route path="/dock/loading" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.DOCK_PROCESS}>
                        <LoadingList userData={userData} />
                    </ProtectedRoute>
                } />

                <Route path="/operations/tracking" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.RESERVATIONS}>
                        <ReservationTracking userData={userData} />
                    </ProtectedRoute>
                } />

                {/* Dock Management - Shared */}
                <Route path="/coordinator/dock-management" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.DOCKS}>
                        <DockManagement userData={userData} />
                    </ProtectedRoute>
                } />

                {/* Admin Routes */}
                <Route path="/admin/users" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.USER_MANAGEMENT}>
                        <UserManagement userData={userData} />
                    </ProtectedRoute>
                } />

                <Route path="/admin/permissions" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.PERMISSION_MANAGEMENT}>
                        <PermissionManagement userData={userData} />
                    </ProtectedRoute>
                } />

                <Route path="/admin/settings" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.SETTINGS}>
                        <Settings />
                    </ProtectedRoute>
                } />

                {/* Debug Route */}
                <Route path="/debug/test" element={<TestRunner />} />

                {/* Reports Routes */}
                <Route path="/analysis/carrier-performance" element={
                    <ProtectedRoute user={user} userData={userData} requiredPermission={PERMISSIONS.VIEW_CARRIER_PERFORMANCE}>
                        <CarrierPerformanceReport userData={userData} />
                    </ProtectedRoute>
                } />

                {/* 404 */}
                <Route path="*" element={<ProtectedRoute user={user} userData={userData}><NotFound /></ProtectedRoute>} />
            </Routes>
        </Router>
    );
};

function App() {
    return (
        <ThemeProvider>
            <ToastProvider>
                <AppContent />
                <ToastContainer />
            </ToastProvider>
        </ThemeProvider>
    );
}

export default App;
